import { Empty, Space } from "antd";
import ReactApexChart from "react-apexcharts";
import { useMemo } from "react";
import { chaveDaCategoria } from "./timelineDaV2";

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(value);
};

const formatDateLocal = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatCurrencyBRL = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getCollectionKey = chaveDaCategoria;

const buildRowLabel = (collection, trackIndex, totalTracks) =>
  totalTracks > 1
    ? `${collection}__track_${String(trackIndex + 1).padStart(2, "0")}`
    : collection;

const getDisplayCollectionLabel = (rowLabel = "") =>
  String(rowLabel).replace(/__track_\d+$/, "");

const assignCollectionTracks = (items) => {
  const groupedItems = new Map();

  items.forEach((item) => {
    const key = getCollectionKey(item);
    if (!groupedItems.has(key)) {
      groupedItems.set(key, []);
    }
    groupedItems.get(key).push(item);
  });

  const trackedItems = [];
  const primaryRowByCollection = {};

  groupedItems.forEach((groupItems, key) => {
    const sortedItems = [...groupItems].sort((a, b) => {
      const aStart = a.calculatedStartDate ?? parseDateValue(a.start);
      const bStart = b.calculatedStartDate ?? parseDateValue(b.start);
      const aEnd =
        a.calculatedEndDate ?? (a.end ? parseDateValue(a.end) : new Date());
      const bEnd =
        b.calculatedEndDate ?? (b.end ? parseDateValue(b.end) : new Date());

      const startDiff = aStart.getTime() - bStart.getTime();
      if (startDiff !== 0) return startDiff;

      const endDiff = aEnd.getTime() - bEnd.getTime();
      if (endDiff !== 0) return endDiff;

      return String(a.zone_description).localeCompare(
        String(b.zone_description),
        "pt-BR"
      );
    });

    const trackEndTimes = [];
    const itemsWithTracks = sortedItems.map((item) => {
      const startDate = item.calculatedStartDate ?? parseDateValue(item.start);
      const endDate =
        item.calculatedEndDate ??
        (item.end ? parseDateValue(item.end) : new Date());
      const startTime = startDate.getTime();
      let trackIndex = trackEndTimes.findIndex(
        (endTime) => startTime > endTime
      );

      if (trackIndex === -1) {
        trackIndex = trackEndTimes.length;
        trackEndTimes.push(endDate.getTime());
      } else {
        trackEndTimes[trackIndex] = endDate.getTime();
      }

      return {
        ...item,
        calculatedStartDate: startDate,
        calculatedEndDate: endDate,
        trackIndex,
      };
    });

    const totalTracks = trackEndTimes.length;

    itemsWithTracks.forEach((item) => {
      const rowLabel = buildRowLabel(
        item.collection,
        item.trackIndex,
        totalTracks
      );

      trackedItems.push({
        ...item,
        rowLabel,
        totalTracks,
      });

      if (!primaryRowByCollection[key]) {
        primaryRowByCollection[key] = rowLabel;
      }
    });
  });

  return {
    trackedItems,
    primaryRowByCollection,
  };
};

const getInclusiveDaysBetweenDates = (startDate, endDate) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) return 0;
  const diffInMs = endDate.getTime() - startDate.getTime();
  if (diffInMs < 0) return 0;
  return Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;
};

const CollectionZoneTimeline = ({
  collectionsData,
  error,
  appliedZones,
  selectedDateRange,
}) => {
  const parseRangeBoundary = (value, boundary) => {
    if (!value) return null;

    if (/^\d{4}-\d{2}$/.test(value)) {
      const [year, month] = value.split("-").map(Number);
      if (!year || !month) return null;
      return boundary === "start"
        ? new Date(year, month - 1, 1, 0, 0, 0, 0)
        : new Date(year, month, 0, 23, 59, 59, 999);
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);
      if (!year || !month || !day) return null;
      return boundary === "start"
        ? new Date(year, month - 1, day, 0, 0, 0, 0)
        : new Date(year, month - 1, day, 23, 59, 59, 999);
    }

    const parsed = parseDateValue(value);
    if (!parsed || Number.isNaN(parsed.getTime())) return null;
    if (boundary === "start") {
      parsed.setHours(0, 0, 0, 0);
    } else {
      parsed.setHours(23, 59, 59, 999);
    }
    return parsed;
  };

  const getSelectedRange = (dateRange) => {
    if (!Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
      return null;
    }
    const startDate = parseRangeBoundary(dateRange[0], "start");
    const endDate = parseRangeBoundary(dateRange[1], "end");
    if (!startDate || !endDate) return null;
    return [startDate.getTime(), endDate.getTime()];
  };
  const selectedRange = getSelectedRange(selectedDateRange);

  const { maxDailyRevenue, minDailyRevenue } = useMemo(() => {
    if (!Array.isArray(collectionsData) || collectionsData.length === 0) {
      return { maxDailyRevenue: 0, minDailyRevenue: 0 };
    }

    const dailyRevenues = collectionsData
      .filter((item) => !item.is_unlinked_only)
      .filter(
        (item) =>
          appliedZones.includes("all") || appliedZones.includes(item.zone)
      )
      .map((item) => {
        const startDate = parseDateValue(item.start);
        const endDate = item.end ? parseDateValue(item.end) : new Date();
        const days = getInclusiveDaysBetweenDates(startDate, endDate);
        return item.revenue / (days || 1);
      });

    if (dailyRevenues.length === 0) {
      return { maxDailyRevenue: 0, minDailyRevenue: 0 };
    }

    return {
      maxDailyRevenue: Math.max(...dailyRevenues),
      minDailyRevenue: Math.min(...dailyRevenues),
    };
  }, [collectionsData, appliedZones]);

  const calculateColorIntensity = (revenue, days) => {
    if (!Array.isArray(collectionsData) || collectionsData.length === 0) {
      return { collection: `rgba(24, 144, 255, 0.1)` };
    }

    if (maxDailyRevenue === minDailyRevenue) {
      return { collection: `rgba(24, 144, 255, 0.5)` };
    }

    const dailyRevenue = revenue / (days || 1);

    const normalized =
      (dailyRevenue - minDailyRevenue) / (maxDailyRevenue - minDailyRevenue);

    const clampedNormalized = Math.max(0, Math.min(1, normalized));

    return {
      collection: `rgba(24, 144, 255, ${0.2 + clampedNormalized * 0.8})`,
    };
  };

  const { series, unlinkedOnlyRowCount } = useMemo(() => {
    const processedData = [];
    const unlinkedRevenueMarkers = [];
    const linkedRevenueMarkers = [];
    const markerDurationMs = 8 * 60 * 60 * 1000;
    const rangeStart = selectedRange ? new Date(selectedRange[0]) : null;
    const rangeEnd = selectedRange ? new Date(selectedRange[1]) : null;

    const zoneLinkedItems = [];
    const unlinkedOnlyItems = [];
    collectionsData.forEach((item) => {
      if (item.is_unlinked_only) {
        unlinkedOnlyItems.push(item);
      } else {
        zoneLinkedItems.push(item);
      }
    });

    const filteredData = zoneLinkedItems
      .filter((item) => {
        if (appliedZones.includes("all")) return true;
        return appliedZones.includes(item.zone);
      })
      .map((item) => {
        const originalStart = parseDateValue(item.start);
        const originalEnd = item.end ? parseDateValue(item.end) : new Date();
        const overlapStart = rangeStart
          ? new Date(Math.max(originalStart.getTime(), rangeStart.getTime()))
          : originalStart;
        const overlapEnd = rangeEnd
          ? new Date(Math.min(originalEnd.getTime(), rangeEnd.getTime()))
          : originalEnd;
        const days = getInclusiveDaysBetweenDates(overlapStart, overlapEnd);
        const dailyRevenue = Number(item.revenue || 0) / (days || 1);
        const collectionSortDailyRevenue = Number(
          item.collection_daily_revenue_in_range ?? dailyRevenue
        );
        const collectionSortRevenue = Number(
          item.collection_revenue_in_range ?? item.revenue ?? 0
        );

        return {
          ...item,
          calculatedDays: days,
          calculatedDailyRevenue: dailyRevenue,
          rowSortDailyRevenue: dailyRevenue,
          rowSortRevenue: Number(item.revenue ?? 0),
          collectionSortDailyRevenue,
          collectionSortRevenue,
          calculatedStartDate: overlapStart,
          calculatedEndDate: overlapEnd,
          isOpenEnded: !item.end,
        };
      })
      .filter((item) => item.calculatedEndDate >= item.calculatedStartDate)
      .sort((a, b) => {
        const revenueDiff =
          b.collectionSortDailyRevenue - a.collectionSortDailyRevenue;

        if (revenueDiff !== 0) return revenueDiff;

        const totalRevenueDiff =
          b.collectionSortRevenue - a.collectionSortRevenue;

        if (totalRevenueDiff !== 0) return totalRevenueDiff;

        return String(a.collection).localeCompare(
          String(b.collection),
          "pt-BR"
        );
      });

    const { trackedItems } = assignCollectionTracks(filteredData);

    const rowMetrics = new Map();
    trackedItems.forEach((item) => {
      const currentMetric = rowMetrics.get(item.rowLabel) || {
        revenue: 0,
        dailyRevenue: 0,
      };

      rowMetrics.set(item.rowLabel, {
        revenue: currentMetric.revenue + Number(item.rowSortRevenue || 0),
        dailyRevenue: Math.max(
          currentMetric.dailyRevenue,
          Number(item.rowSortDailyRevenue || 0)
        ),
      });
    });

    const sortedTrackedItems = [...trackedItems].sort((a, b) => {
      const rowMetricA = rowMetrics.get(a.rowLabel) || {
        revenue: 0,
        dailyRevenue: 0,
      };
      const rowMetricB = rowMetrics.get(b.rowLabel) || {
        revenue: 0,
        dailyRevenue: 0,
      };

      const revenueDiff = rowMetricB.revenue - rowMetricA.revenue;
      if (revenueDiff !== 0) return revenueDiff;

      const dailyRevenueDiff =
        rowMetricB.dailyRevenue - rowMetricA.dailyRevenue;
      if (dailyRevenueDiff !== 0) return dailyRevenueDiff;

      const collectionDiff = String(a.collection).localeCompare(
        String(b.collection),
        "pt-BR"
      );
      if (collectionDiff !== 0) return collectionDiff;

      const zoneDiff = String(a.zone_description).localeCompare(
        String(b.zone_description),
        "pt-BR"
      );
      if (zoneDiff !== 0) return zoneDiff;

      return a.calculatedStartDate.getTime() - b.calculatedStartDate.getTime();
    });

    const unlinkedOnlyWithMetrics = unlinkedOnlyItems.map((item) => {
      const unlinkedPoints = Array.isArray(item.unlinked_revenue_points)
        ? item.unlinked_revenue_points
        : [];
      const totalRevenue = unlinkedPoints.reduce(
        (sum, p) => sum + Number(p.revenue || 0),
        0
      );
      const daysCount = unlinkedPoints.length || 1;
      const dailyRevenue = totalRevenue / daysCount;
      const rowLabel = item.collection || `Categoria ${item.legacy_id}`;
      return {
        ...item,
        rowLabel,
        _sortRevenue: totalRevenue,
        _sortDailyRevenue: dailyRevenue,
      };
    });

    const sortedCombinedRows = [
      ...sortedTrackedItems.map((item) => {
        const metrics = rowMetrics.get(item.rowLabel) || {
          revenue: 0,
          dailyRevenue: 0,
        };
        return {
          ...item,
          _sortRevenue: metrics.revenue,
          _sortDailyRevenue: metrics.dailyRevenue,
        };
      }),
      ...(appliedZones.includes("all") ? unlinkedOnlyWithMetrics : []),
    ].sort((a, b) => {
      const revenueDiff = b._sortRevenue - a._sortRevenue;
      if (revenueDiff !== 0) return revenueDiff;
      const dailyRevenueDiff = b._sortDailyRevenue - a._sortDailyRevenue;
      if (dailyRevenueDiff !== 0) return dailyRevenueDiff;
      return String(a.collection).localeCompare(String(b.collection), "pt-BR");
    });

    const visiblePrimaryRowByCollection = {};
    sortedCombinedRows.forEach((item) => {
      const collectionKey = getCollectionKey(item);
      if (!visiblePrimaryRowByCollection[collectionKey]) {
        visiblePrimaryRowByCollection[collectionKey] = item.rowLabel;
      }
    });

    const pushUnlinkedMarkers = (rowLabel, item) => {
      const unlinkedPoints = Array.isArray(item.unlinked_revenue_points)
        ? item.unlinked_revenue_points
        : [];
      unlinkedPoints.forEach((point) => {
        const pointDate = parseDateValue(point.date);
        if (!pointDate || Number.isNaN(pointDate.getTime())) return;
        if (
          (rangeStart && pointDate < rangeStart) ||
          (rangeEnd && pointDate > rangeEnd)
        )
          return;
        unlinkedRevenueMarkers.push({
          x: rowLabel,
          y: [pointDate.getTime(), pointDate.getTime() + markerDurationMs],
          collection: item.collection,
          dateLabel: formatDateLocal(pointDate),
          fillColor: "#ff7875",
          revenue: Number(point.revenue) || 0,
          zone_description: item.zone_description || "Sem zona",
        });
      });
    };

    sortedCombinedRows.forEach((item) => {
      if (item.is_unlinked_only) {
        const firstPointDate =
          Array.isArray(item.unlinked_revenue_points) &&
          item.unlinked_revenue_points.length > 0
            ? parseDateValue(item.unlinked_revenue_points[0].date)
            : null;
        const phantomTs = rangeStart
          ? rangeStart.getTime()
          : firstPointDate
            ? firstPointDate.getTime()
            : Date.now();
        processedData.push({
          x: item.rowLabel,
          y: [phantomTs, phantomTs + 1],
          fillColor: "rgba(0,0,0,0)",
          strokeColor: "rgba(0,0,0,0)",
          collection: item.collection,
          revenue: 0,
          dailyRevenue: 0,
          isPhantom: true,
        });
        pushUnlinkedMarkers(item.rowLabel, item);
      } else {
        const colors = calculateColorIntensity(
          item.revenue,
          item.calculatedDays
        );
        const rowLabel = item.rowLabel;

        processedData.push({
          x: rowLabel,
          y: [
            item.calculatedStartDate.getTime(),
            item.calculatedEndDate.getTime(),
          ],
          startLabel: formatDateLocal(item.calculatedStartDate),
          endLabel: formatDateLocal(item.calculatedEndDate),
          days: item.calculatedDays,
          fillColor: colors.collection,
          collection: item.collection,
          revenue: item.revenue,
          dailyRevenue: item.calculatedDailyRevenue,
          legacyId: item.legacy_id,
          zone: item.zone,
          zone_description: item.zone_description,
          isOpenEnded: item.isOpenEnded,
        });

        const linkedPoints = Array.isArray(item.linked_revenue_points)
          ? item.linked_revenue_points
          : [];
        linkedPoints.forEach((point) => {
          const pointDate = parseDateValue(point.date);
          if (!pointDate || Number.isNaN(pointDate.getTime())) return;
          if (
            (rangeStart && pointDate < rangeStart) ||
            (rangeEnd && pointDate > rangeEnd)
          )
            return;
          linkedRevenueMarkers.push({
            x: rowLabel,
            y: [pointDate.getTime(), pointDate.getTime() + markerDurationMs],
            collection: item.collection,
            dateLabel: formatDateLocal(pointDate),
            fillColor: "#0d47a1",
            revenue: Number(point.revenue) || 0,
            zone_description: item.zone_description,
          });
        });

        if (
          visiblePrimaryRowByCollection[getCollectionKey(item)] === rowLabel
        ) {
          pushUnlinkedMarkers(rowLabel, item);
        }
      }
    });

    return {
      series: [
        {
          name: "Categorias",
          type: "rangeBar",
          data: processedData,
        },
        ...(unlinkedRevenueMarkers.length > 0
          ? [
              {
                name: "Faturamento fora da vigencia",
                type: "rangeBar",
                data: unlinkedRevenueMarkers,
              },
            ]
          : []),
        ...(linkedRevenueMarkers.length > 0
          ? [
              {
                name: "Vendas no dia",
                type: "rangeBar",
                data: linkedRevenueMarkers,
              },
            ]
          : []),
      ],
      unlinkedOnlyRowCount: appliedZones.includes("all")
        ? unlinkedOnlyItems.length
        : 0,
    };
  }, [
    collectionsData,
    appliedZones,
    selectedRange,
    maxDailyRevenue,
    minDailyRevenue,
  ]);

  const options = {
    chart: {
      height: 350,
      type: "rangeBar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    colors: ["#1890ff", "#ff7875", "#0d47a1"],
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "30px",
        rangeBarGroupRows: true,
      },
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
        datetimeFormatter: {
          year: "yyyy",
          month: "MMM/yy",
          day: "dd/MMM",
        },
        offsetY: 3,
      },
      position: "top",
      axisTicks: {
        show: false,
      },
      min: selectedRange ? selectedRange[0] : undefined,
      max: selectedRange ? selectedRange[1] : undefined,
    },
    yaxis: {
      labels: {
        formatter: function (value) {
          return getDisplayCollectionLabel(value);
        },
        style: {
          fontSize: "12px",
          colors: "#000000",
        },
        maxWidth: 300,
        minWidth: 150,
      },
      tickAmount: "dataPoints",
    },
    grid: {
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    tooltip: {
      custom: function (opts) {
        const datapoint =
          opts?.ctx?.w?.config?.series?.[opts.seriesIndex]?.data?.[
            opts.dataPointIndex
          ];

        if (!datapoint || datapoint.isPhantom) {
          return "";
        }

        const seriesName = opts?.w?.config?.series?.[opts.seriesIndex]?.name;

        if (seriesName === "Faturamento fora da vigencia") {
          return `
            <div class="apexcharts-tooltip-title" style="padding: 8px">
              <b>${datapoint.collection}</b><br/>
              Data: ${datapoint.dateLabel}<br/>
              Faturamento fora da vigencia em zona: R$ ${formatCurrencyBRL(
                datapoint.revenue
              )}
            </div>
          `;
        }

        if (seriesName === "Vendas no dia") {
          return `
            <div class="apexcharts-tooltip-title" style="padding: 8px">
              <b>${datapoint.collection}</b><br/>
              Data: ${datapoint.dateLabel}<br/>
              Zona: ${datapoint.zone_description}<br/>
              Faturamento no dia: R$ ${formatCurrencyBRL(datapoint.revenue)}
            </div>
          `;
        }

        const todayLabel = formatDateLocal(new Date());
        const showAtual =
          datapoint.isOpenEnded && datapoint.endLabel === todayLabel;

        return `
          <div class="apexcharts-tooltip-title" style="padding: 8px">
            <b>${datapoint.collection}</b><br/>
            Zona: ${datapoint.zone_description}<br/>
            Periodo: ${datapoint.startLabel} - ${
              showAtual ? "Atual" : datapoint.endLabel
            }<br/>
            Duracao: ${datapoint.days} dias<br/>
            Faturamento total: R$ ${datapoint.revenue.toLocaleString(
              "pt-BR"
            )}<br/>
            Media diaria: R$ ${Math.round(
              datapoint.dailyRevenue
            ).toLocaleString("pt-BR")}/dia
            </div>
        `;
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [0],
      formatter: function (val, opts) {
        const datapoint =
          opts?.w?.config?.series?.[opts.seriesIndex]?.data?.[
            opts.dataPointIndex
          ];
        return datapoint?.zone_description || "";
      },
      style: {
        colors: ["#ffffff"],
        fontSize: "12px",
      },
      background: {
        enabled: true,
        foreColor: "#000000",
        padding: 4,
        borderRadius: 5,
        borderWidth: 1,
        borderColor: "#ddd",
        opacity: 0.9,
      },
      position: "center",
      offsetX: 15,
    },
  };

  const chartHeight = useMemo(() => {
    const rangeStart = selectedRange ? new Date(selectedRange[0]) : null;
    const rangeEnd = selectedRange ? new Date(selectedRange[1]) : null;
    const zoneLinkedData = collectionsData
      .filter((item) => !item.is_unlinked_only)
      .filter((item) => {
        if (appliedZones.includes("all")) return true;
        return appliedZones.includes(item.zone);
      })
      .filter((item) => {
        if (!rangeStart || !rangeEnd) return true;
        const originalStart = parseDateValue(item.start);
        const originalEnd = item.end ? parseDateValue(item.end) : new Date();
        return (
          originalEnd.getTime() >= rangeStart.getTime() &&
          originalStart.getTime() <= rangeEnd.getTime()
        );
      });
    const { trackedItems } = assignCollectionTracks(zoneLinkedData);
    const uniqueRows = new Set(trackedItems.map((item) => item.rowLabel)).size;
    const totalRows = uniqueRows + (unlinkedOnlyRowCount || 0);

    return Math.max(350, totalRows * 50);
  }, [collectionsData, appliedZones, selectedRange, unlinkedOnlyRowCount]);

  if (error) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span style={{ color: "#ff4d4f" }}>
            Erro ao carregar dados das categorias. Tente novamente mais tarde.
          </span>
        }
      />
    );
  }

  const hasDataForSelectedZones = collectionsData.some((item) => {
    if (item.is_unlinked_only) return appliedZones.includes("all");
    return appliedZones.includes("all") || appliedZones.includes(item.zone);
  });

  if (!hasDataForSelectedZones) {
    return (
      <Empty
        description="Nenhuma categoria encontrada para as zonas selecionadas"
        style={{ padding: "50px" }}
      />
    );
  }

  return (
    <div style={{ width: "100%", position: "relative" }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <ReactApexChart
          options={options}
          series={series}
          type="rangeBar"
          height={chartHeight}
        />
      </Space>
    </div>
  );
};

export default CollectionZoneTimeline;
